import { palettePrimaryMain } from '@/theme/designTokens'
import {
  ROUTING_CONTINUE_PRIMARY_BTN_SX,
  ROUTING_OUTLINED_BTN_SX,
  routingNavy,
  routingPrimary,
  routingSubtitle,
} from '@/features/instruments/routing/routingFigmaTokens'
import {
  PUBLISH_BUDGET_INNER_CARD_PADDING_BOTTOM_PX,
  PUBLISH_BUDGET_INNER_CARD_PADDING_TOP_PX,
  PUBLISH_BUDGET_INNER_CARD_PADDING_X_PX,
  PUBLISH_BUDGET_INPUT_HEIGHT_PX,
  PUBLISH_BUDGET_INPUT_PADDING_X_PX,
  PUBLISH_BUDGET_PANEL_PADDING_BOTTOM_PX,
  PUBLISH_BUDGET_PANEL_PADDING_TOP_PX,
  PUBLISH_BUDGET_PANEL_PADDING_X_PX,
} from './publishWizardLayout'

export const publishNavy = routingNavy
export const publishInk = '#0d1b2a'
export const publishSubtitle = routingSubtitle
export { routingSecondary as publishSecondary } from '@/features/instruments/routing/routingFigmaTokens'
export const publishPrimary = routingPrimary

/** Page chrome — Figma `4687:5472` / `4687:5474` (Head_bold_24 + Body_reg_14) */
export const PUBLISH_PAGE_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '32px',
  color: publishInk,
  letterSpacing: '0.0703px',
} as const

export const PUBLISH_PAGE_SUBTITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: publishSubtitle,
} as const

/** In-card step instruction — Figma Body_reg_14 under step title */
export const PUBLISH_STEP_INSTRUCTION_SX = {
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 400,
  color: '#6b7a90',
} as const

/** Distribution Channels step sub-heading — Figma `4687:6482` */
export const PUBLISH_STEP_SUBTITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  lineHeight: '19.5px',
  fontWeight: 400,
  color: '#64748b',
} as const

/** Distribution channel cards — Figma `4687:6529` / `4687:6519` */
export const publishChannelCardSelectedBg = '#f0f8fd'
export const publishChannelCardBorder = '#e4e9f0'
/** Figma selected card — `border border-[#008cd0]` (1px, not 2px) */
export const publishChannelCardSelectedBorder = '#008cd0'
export const publishChannelCardSelectedShadow = '1px 2px 9px #008cd0'

/** Instrument Type cards — Figma `5326:9628` / `4687:6629` */
export const publishInstrumentTypeCardMinHeightPx = 194
export const publishInstrumentTypeCardPadding = '14px 17px 17px 29px'
export const publishInstrumentTypeIconSizePx = 76
/** Icon bottom → title top (~88px text rail − 90px icon bottom) */
export const publishInstrumentTypeIconToTitlePx = 4
/** Title block (42px) → description */
export const publishInstrumentTypeTitleToDescriptionPx = 4
/** Figma `drop-shadow-[1px_2px_4.5px_#008cd0]` */
export const publishInstrumentTypeCardSelectedShadow = '1px 2px 4.5px #008cd0'

/** Instrument Type card content — Figma `5326:9628` */
export const PUBLISH_INSTRUMENT_TYPE_TITLE_SX = {
  fontWeight: 700,
  fontSize: '28px',
  lineHeight: '42px',
  mb: `${publishInstrumentTypeTitleToDescriptionPx}px`,
  color: publishPrimary,
  pointerEvents: 'none',
} as const

export const PUBLISH_INSTRUMENT_TYPE_DESCRIPTION_SX = {
  fontSize: '13px',
  lineHeight: '19.5px',
  color: publishSubtitle,
  pointerEvents: 'none',
  maxWidth: '401px',
} as const

/** Notification preview section — Figma `4687:6488` / `4687:6547` */
export const publishPreviewSectionTitleColor = '#6b7a90'
export const publishPreviewSectionSubtitleColor = '#a0abbf'
export const publishPreviewPanelBg = '#f8fafc'
export const publishPreviewPushLabelColor = '#f59e0b'

/** Wizard footer Back link — Figma `5074:7922` */
export const PUBLISH_WIZARD_BACK_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#4a5565',
  textTransform: 'none',
  minWidth: 'auto',
  p: 0,
  '&:hover': {
    bgcolor: 'transparent',
    color: '#4a5565',
    textDecoration: 'underline',
  },
} as const

/** Publish workspace breadcrumb — Figma secondary description text */
export const PUBLISH_BREADCRUMB_TEXT_SX = {
  color: 'var(--Secondary-text-descriptions, #64748B)',
  fontSize: '14px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  lineHeight: '20px',
  wordWrap: 'break-word',
} as const

/** Persona summary card — Figma `4687:5532` */
export const publishPersonaCardBg = '#f0f8fd'
export const publishPersonaCardBorder = '#c7ebf9'
export const publishPersonaAgeColor = '#008cd0'
export const publishPersonaRegionColor = '#006192'
export const publishPersonaTagColor = '#3b82f6'

/** Match count card — Figma `4687:5542` */
export const publishMatchCardBg = '#edfdf5'
export const publishMatchCardBorder = '#daf5e4'
export const publishMatchCountGreen = '#0d9467'
export const publishMatchHintBlue = '#3b82f6'

/** Filter accordion — Figma `4687:5559` */
export const publishFilterPanelBorder = '#93c5fd'
export const publishFilterHeaderBg = '#f0f8fd'
export const publishFilterChevronBg = '#93c5fd'
export const publishFilterChevronBorder = '#c7ebf9'

/** Stepper — Figma `5311:9728` / `5074:7434` */
export const publishStepperInactiveNumber = '#7a797e'
export const publishStepperLabelActive = '#008cd0'
export const publishStepperLabelInactive = '#5a5656'

/** Figma active step circle — soft elevation under current step */
export const PUBLISH_STEPPER_ACTIVE_CIRCLE_DROP_SHADOW =
  'drop-shadow(0px 4px 6px rgba(25, 69, 107, 0.4)) drop-shadow(0px 8px 14px rgba(4, 24, 63, 0.22))'

/** Figma step labels `5074:7437` */
export const PUBLISH_STEPPER_LABEL_SX = {
  fontFamily: 'Roboto, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '10px',
  letterSpacing: '0.28px',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  wordWrap: 'normal',
} as const

export const publishInputBorder = '#e4e9f0'
export const publishWarningAmber = '#F59E0B'
export const publishErrorRed = '#EF4444'

export { ROUTING_OUTLINED_BTN_SX as PUBLISH_OUTLINED_BTN_SX } from '@/features/instruments/routing/routingFigmaTokens'

/** Target Audience actions — Save Draft / Next (`#008CD0`). */
export const PUBLISH_ACTION_TEAL_OUTLINED_BTN_SX = {
  ...ROUTING_OUTLINED_BTN_SX,
  color: palettePrimaryMain,
  borderColor: palettePrimaryMain,
  '&:hover': {
    bgcolor: 'rgba(0, 140, 208, 0.04)',
    borderColor: palettePrimaryMain,
    color: palettePrimaryMain,
    boxShadow: 'none',
  },
} as const

/** Figma `4687:5934` — Clear All Filters (navy outline). */
export const PUBLISH_CLEAR_FILTERS_BTN_SX = {
  textTransform: 'none',
  borderRadius: '8px',
  fontFamily: 'Roboto, Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  height: 40,
  minHeight: 40,
  py: '10px',
  px: '16px',
  bgcolor: '#fff',
  color: publishNavy,
  borderColor: publishNavy,
  boxShadow: 'none',
  flexShrink: 0,
  '&:hover': {
    bgcolor: 'rgba(4, 24, 63, 0.04)',
    borderColor: publishNavy,
    color: publishNavy,
    boxShadow: 'none',
  },
} as const

/** Figma `4687:6413` — Restore Ai Suggestions (filled navy after filters cleared). */
export const PUBLISH_RESTORE_AI_BTN_SX = {
  ...PUBLISH_CLEAR_FILTERS_BTN_SX,
  bgcolor: publishNavy,
  color: '#fff',
  borderColor: publishNavy,
  '&:hover': {
    bgcolor: '#032a52',
    borderColor: publishNavy,
    color: '#fff',
    boxShadow: 'none',
  },
} as const

export const PUBLISH_PRIMARY_BTN_SX = {
  ...ROUTING_CONTINUE_PRIMARY_BTN_SX,
  bgcolor: palettePrimaryMain,
  '&:hover': { bgcolor: '#0076b0', boxShadow: 'none' },
} as const

/**
 * Budget tab inline Next / Confirm — Figma `4687:6750` (62×40, hug contents).
 * M3 label/large: Roboto Medium 14 / 20, tracking 0.1px, #008CD0 fill, 8px radius.
 */
export const PUBLISH_BUDGET_TAB_ACTION_BTN_SX = {
  fontFamily: 'Roboto, Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  textTransform: 'none',
  borderRadius: '8px',
  height: 40,
  minHeight: 40,
  px: '16px',
  py: '10px',
  minWidth: 'unset',
  width: 'auto',
  bgcolor: palettePrimaryMain,
  color: '#fff',
  border: 'none',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#0076b0', boxShadow: 'none' },
} as const

export const PUBLISH_BUDGET_TAB_ACTION_BTN_DISABLED_SX = {
  ...PUBLISH_BUDGET_TAB_ACTION_BTN_SX,
  bgcolor: '#9ca3af',
  '&:hover': { bgcolor: '#9ca3af', boxShadow: 'none' },
  '&.Mui-disabled': {
    bgcolor: '#9ca3af',
    color: '#fff',
  },
} as const

/** Campaign Budget outer Next when gated — Figma `5074:8563` */
export const PUBLISH_PRIMARY_BTN_DISABLED_SX = {
  ...ROUTING_CONTINUE_PRIMARY_BTN_SX,
  bgcolor: '#9ca3af',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#9ca3af', boxShadow: 'none' },
  '&.Mui-disabled': {
    bgcolor: '#9ca3af',
    color: '#fff',
  },
} as const

/** Campaign Budget tab bar — Figma `4687:6833` */
export const publishBudgetTabActiveBg = '#008cd0'
export const publishBudgetTabInactiveText = '#6f6b6b'
export const publishBudgetTabInactiveTextAlt = '#5a5656'
export const publishBudgetTabBorder = '#008cd0'

/** Estimated cost / status cards — Figma `4687:6965` */
export const publishBudgetSuccessBg = '#f0faf6'
export const publishBudgetSuccessBorder = '#b6e8d5'
export const publishBudgetSuccessGreen = '#0e8a5f'

/** Estimated Instrument Cost card — outside campaign budget (wallet still sufficient). */
export const publishBudgetWarningBg = '#fffbeb'
export const publishBudgetWarningBorder = '#fde68a'
export const publishBudgetWarningAmber = '#f59e0b'
/** Define Campaign Budget amount — Figma `4687:7033` */
export const publishBudgetDefineAmountGreen = '#0d9467'
export const publishBudgetBreakdownHeaderBg = '#f8fafc'
export const publishBudgetBreakdownBorder = '#e4e9f0'
/** Breakdown outer card stroke — Figma `4687:6959` widget border */
export const publishBudgetBreakdownCardBorder = '#daf5e4'
export const publishBudgetBreakdownRowDivider = '#f8fafc'
export const publishBudgetProgressTrack = '#e4e9f0'
export const publishBudgetTopUpLink = '#3b82f6'
export const publishBudgetHelperTeal = '#3b82f6'

export const PUBLISH_BUDGET_TAB_LABEL_SX = {
  fontFamily: 'Roboto, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '10px',
  letterSpacing: '0.28px',
  textTransform: 'capitalize',
} as const

/** Inner tab panel — Figma `4687:6737` RewardCard */
export const publishBudgetInnerCardBorder = '#e8edf2'
/** Required asterisk — Figma `4687:6741` */
export const publishBudgetRequiredAsterisk = '#e24b4a'

export const PUBLISH_BUDGET_INNER_CARD_SX = {
  bgcolor: '#fff',
  border: `1px solid ${publishBudgetInnerCardBorder}`,
  borderRadius: '14px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04)',
  pt: `${PUBLISH_BUDGET_INNER_CARD_PADDING_TOP_PX}px`,
  px: `${PUBLISH_BUDGET_INNER_CARD_PADDING_X_PX}px`,
  pb: `${PUBLISH_BUDGET_INNER_CARD_PADDING_BOTTOM_PX}px`,
  boxSizing: 'border-box',
} as const

/** Secondary line under reward title — Figma `4687:6846` */
export const PUBLISH_BUDGET_FIELD_SUBTITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  lineHeight: '19.5px',
  fontWeight: 400,
  color: publishSubtitle,
} as const

/** Reward minimum hint — Figma `4687:6852` (10px) */
export const PUBLISH_BUDGET_REWARD_HELPER_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  lineHeight: '19.5px',
  fontWeight: 400,
  color: publishBudgetHelperTeal,
} as const

/** Tab field title — Figma `4687:6740` / `4687:6741` */
export const PUBLISH_BUDGET_FIELD_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  color: publishInk,
} as const

/** Field question under tab title — Figma `4687:6739` */
export const PUBLISH_BUDGET_FIELD_QUESTION_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  lineHeight: '22.5px',
  fontWeight: 400,
  color: publishInk,
} as const

/** Helper under number input — Figma `4687:6747` */
export const PUBLISH_BUDGET_FIELD_HELPER_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  lineHeight: '18px',
  fontWeight: 400,
  color: publishBudgetHelperTeal,
} as const

/** Number / currency field — Figma `4687:6744` */
export const PUBLISH_BUDGET_NUMBER_INPUT_SX = {
  m: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    minHeight: `${PUBLISH_BUDGET_INPUT_HEIGHT_PX}px`,
    px: `${PUBLISH_BUDGET_INPUT_PADDING_X_PX}px`,
    '& fieldset': { borderColor: publishInputBorder },
  },
  '& .MuiInputBase-input': {
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '27px',
    py: '15px',
    px: 0,
    boxSizing: 'border-box',
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0,
    },
} as const

/** Custom up/down stepper on budget inputs — matches reward field `4687:6744` */
export const PUBLISH_BUDGET_INPUT_STEPPER_ICON_SX = {
  fontSize: 16,
  color: publishInk,
} as const

/** Reward field stepper — slightly heavier than response target */
export const PUBLISH_BUDGET_INPUT_STEPPER_ICON_EMPHASIZED_SX = {
  fontSize: 18,
  color: publishInk,
  fontWeight: 600,
} as const

/** Budget step tab region — Figma `4687:6757` wraps tab bar + tab body */
export const publishBudgetPanelBorder = '#e4e9f0'
/** Campaign Budget breakdown shell — Figma `4687:6959` */
export const PUBLISH_BUDGET_BREAKDOWN_CARD_SX = {
  bgcolor: '#fff',
  border: `1px solid ${publishBudgetBreakdownCardBorder}`,
  borderRadius: '14px',
  overflow: 'hidden',
  boxSizing: 'border-box',
} as const

export const PUBLISH_BUDGET_POT_CARD_SX = {
  bgcolor: '#fff',
  border: `1px solid ${publishPersonaCardBorder}`,
  borderRadius: '12px',
  overflow: 'hidden',
  boxSizing: 'border-box',
} as const

export const PUBLISH_BUDGET_PANEL_SX = {
  border: `1px solid ${publishBudgetPanelBorder}`,
  borderRadius: '10px',
  bgcolor: '#fff',
  px: `${PUBLISH_BUDGET_PANEL_PADDING_X_PX}px`,
  pt: `${PUBLISH_BUDGET_PANEL_PADDING_TOP_PX}px`,
  pb: `${PUBLISH_BUDGET_PANEL_PADDING_BOTTOM_PX}px`,
  mb: '32px',
} as const

/** Filter panel header title — Figma `4687:6113` (navy, not teal). */
export const PUBLISH_PANEL_TITLE_SX = {
  fontWeight: 600,
  fontSize: '11px',
  lineHeight: '16.5px',
  letterSpacing: '0.275px',
  textTransform: 'uppercase',
  color: publishNavy,
  mb: 0,
} as const

/** Demographic filter section card shell — Figma `5310:9678` / `4687:6052`. */
export const PUBLISH_FILTER_SECTION_CARD_SX = {
  bgcolor: '#fff',
  border: `1px solid ${publishFilterPanelBorder}`,
  borderRadius: '14px',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
} as const

/**
 * Header strip `4687:6053` — fill only; **no border** (card owns the single `#93c5fd` stroke).
 * A second border on the header caused a double/highlighted line at the top corners.
 */
export const PUBLISH_FILTER_SECTION_HEADER_SX = {
  minHeight: '49px',
  height: '49px',
  bgcolor: publishFilterHeaderBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  px: '32px',
  flexShrink: 0,
  boxSizing: 'border-box',
  cursor: 'pointer',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `inset 0 0 0 2px ${publishPrimary}`,
  },
} as const

export const PUBLISH_FILTER_SECTION_BODY_SX = {
  px: '32px',
  pt: 0,
  pb: '20px',
  bgcolor: '#fff',
  boxSizing: 'border-box',
  width: '100%',
} as const

/** Preview & Publish — Figma `5074:9521` */
export const publishPreviewCardBorder = '#e4e9f0'
export const publishPreviewRowBorder = '#f8fafc'
export const publishPreviewLabelColor = '#6b7a90'
export const publishPreviewDetailsHeaderBg = '#e6f4fb'
export const publishPreviewRoutingHeaderBg = '#fefae2'
export const publishPreviewRoutingIconBg = '#f59e0b'
export const publishPreviewAudienceHeaderBg = '#fae6dc'
export const publishPreviewAudienceIconBg = '#df6534'
export const publishPreviewQuestionsHeaderBg = '#ede9ff'
export const publishPreviewQuestionsIconBg = '#bc24ca'
export const publishPreviewBudgetHeaderBg = '#daf5e4'
export const publishPreviewBudgetIconBg = '#10b981'
export const publishPreviewPoolPillBg = '#f3eeff'
export const publishPreviewPoolPillColor = '#8b5cf6'
/** Audience — distribution channel pills (`4687:7480`–`4687:7485`) */
export const publishPreviewChannelSmsPillBg = '#e6f4fb'
export const publishPreviewChannelEmailPillBg = '#eef2ff'
export const publishPreviewChannelEmailPillColor = '#6366f1'
export const publishPreviewChannelPushPillBg = '#fef3c7'
export const publishPreviewQuestionsLinkBg = '#e6f4fb'
/** Budget Details — Instrument Type pill (`4687:7594`) */
export const publishPreviewInstrumentTypePillBg = '#faeeda'
export const publishPreviewInstrumentTypePillBorder = '#fae6dc'
export const publishPreviewInstrumentTypePillColor = '#8b5719'
/** Figma `5397:11164` — no-reward instrument type pill */
export const publishPreviewInstrumentTypeNoRewardPillBg = '#e4e9f0'
export const publishPreviewInstrumentTypeNoRewardPillBorder = '#e2e8f0'
export const publishPreviewInstrumentTypeNoRewardPillColor = '#64748b'

export const publishPreviewBudgetTileBg = '#f5f4ed'
export const publishPreviewWalletBarBg = '#e6f4fb'

/** Budget Details tiles — Figma `4687:7604`–`4687:7624` */
export const PUBLISH_PREVIEW_BUDGET_TILE_LABEL_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '21px',
  color: publishInk,
} as const

export const PUBLISH_PREVIEW_BUDGET_VALUE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  lineHeight: '27px',
  color: '#101828',
} as const

export const PUBLISH_PREVIEW_BUDGET_TOTAL_COST_VALUE_SX = {
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  color: publishInk,
} as const

export const PUBLISH_PREVIEW_WALLET_BALANCE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '22px',
  lineHeight: '21px',
  color: publishPrimary,
} as const
export const publishPreviewReadyPanelGradient =
  'linear-gradient(180deg, #041e42 69.72%, #172a48 95.86%, #172946 100%)'
export const publishPreviewReadyMetricBg = 'rgba(0, 140, 208, 0.1)'
export const publishPreviewReadyMetricTileBg = 'rgba(255, 255, 255, 0.06)'
export const publishPreviewReadyDisclaimerBannerBg = 'rgba(0, 140, 208, 0.1)'
export const publishPreviewReadyDisclaimerIconWellBg = 'rgba(0, 97, 146, 0.35)'

export const PUBLISH_READY_PANEL_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '28px',
  letterSpacing: '-0.4492px',
  color: '#fff',
  textAlign: 'center',
} as const

export const PUBLISH_READY_METRIC_VALUE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: publishPrimary,
  textAlign: 'left',
} as const

export const PUBLISH_READY_METRIC_LABEL_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 300,
  fontSize: '12px',
  lineHeight: '20px',
  color: '#fff',
  textAlign: 'left',
} as const

export const PUBLISH_READY_DISCLAIMER_BANNER_TEXT_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '21.125px',
  color: '#f8fafc',
  textAlign: 'left',
} as const

export const PUBLISH_READY_PUBLISH_BTN_SX = {
  ...PUBLISH_PRIMARY_BTN_SX,
  minWidth: 363,
  width: { xs: '100%', sm: 363 },
  maxWidth: '100%',
  height: 56,
  py: '16px',
  px: '24px',
  borderRadius: '8px',
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 500,
  letterSpacing: '0.15px',
  textTransform: 'none',
  boxShadow: 'none',
} as const
export const publishPreviewSuccessSubtitleColor = '#008cd0'
export const publishSuccessModalBorder = '#bfc7d2'

/** Figma `4687:7943` — celebration art (279 × 186). */
export const PUBLISH_SUCCESS_ILLUSTRATION_WIDTH_PX = 279
export const PUBLISH_SUCCESS_ILLUSTRATION_HEIGHT_PX = 186

export const publishSuccessModalContentSx = {
  pt: '26px',
  pb: '32px',
  px: '47px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const

export const publishSuccessIllustrationSx = {
  width: `${PUBLISH_SUCCESS_ILLUSTRATION_WIDTH_PX}px`,
  maxWidth: '100%',
  height: `${PUBLISH_SUCCESS_ILLUSTRATION_HEIGHT_PX}px`,
  objectFit: 'contain',
  flexShrink: 0,
  mb: '20px',
} as const

export const publishSuccessTextStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: 546,
  mb: '28px',
} as const

export const publishSuccessCountdownSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '20px',
  color: publishPreviewSuccessSubtitleColor,
  m: 0,
  mt: '12px',
} as const

/** Publish success modal — Figma `5074:10152` / `4687:7932` */
export const PUBLISH_SUCCESS_MODAL_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '32px',
  lineHeight: '40px',
  letterSpacing: '0.4063px',
  color: '#0f172a',
  m: 0,
  mb: '12px',
} as const

export const PUBLISH_SUCCESS_MODAL_SUBTITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '21.125px',
  color: publishPreviewSuccessSubtitleColor,
  m: 0,
  mb: '24px',
} as const

export const PUBLISH_SUCCESS_MODAL_BODY_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '21.125px',
  color: '#0f172a',
  m: 0,
  whiteSpace: 'pre-line',
} as const

export const PUBLISH_SUCCESS_BACK_LOI_BTN_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: 56,
  height: 56,
  flex: 1,
  maxWidth: 265,
  bgcolor: palettePrimaryMain,
  color: '#f8fafc',
  px: '24px',
  py: '16px',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#0076b0', boxShadow: 'none' },
} as const

export const PUBLISH_SUCCESS_HOME_BTN_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: 56,
  height: 56,
  flex: 1,
  maxWidth: 265,
  border: `1px solid ${publishPrimary}`,
  color: publishPrimary,
  bgcolor: '#fff',
  px: '16px',
  py: '10px',
  boxShadow: 'none',
  '&:hover': {
    borderColor: publishPrimary,
    bgcolor: 'rgba(0, 140, 208, 0.04)',
    boxShadow: 'none',
  },
} as const

/** Figma signup-publish success — outlined copy pill above Dashboard / Home. */
export const PUBLISH_SUCCESS_COPY_LINK_BTN_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  textTransform: 'none',
  borderRadius: '999px',
  minHeight: 40,
  height: 40,
  px: '16px',
  py: '8px',
  mb: '16px',
  border: '1px solid #cbd5e1',
  color: publishPrimary,
  bgcolor: '#fff',
  boxShadow: 'none',
  '&:hover': {
    borderColor: publishPrimary,
    bgcolor: 'rgba(0, 140, 208, 0.04)',
    boxShadow: 'none',
  },
} as const

export const PUBLISH_SUCCESS_LINK_COPIED_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  minHeight: 40,
  mb: '16px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#0f172a',
} as const

export const PUBLISH_SUCCESS_LINK_COPIED_ICON_SX = {
  fontSize: 20,
  color: '#22c55e',
} as const

export const PUBLISH_PREVIEW_SECTION_TITLE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: publishInk,
} as const

export const PUBLISH_PREVIEW_ROW_LABEL_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '21px',
  color: publishPreviewLabelColor,
} as const

export const PUBLISH_PREVIEW_ROW_VALUE_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '21px',
  color: publishInk,
} as const

/** Questions Summary count — Figma `4687:7496` (13px regular, not semibold). */
export const PUBLISH_PREVIEW_QUESTION_COUNT_SX = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '19.5px',
  color: publishInk,
} as const

export const PUBLISH_FIELD_LABEL_SX = {
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '18px',
  color: publishSubtitle,
  mb: '6px',
} as const

/** Chevron in location dropdown — visual ~16px inside 40px field (Figma frame is 24px; scaled down in UI). */
export const PUBLISH_FILTER_SELECT_ICON_SIZE_PX = 16

/** Location / filter dropdown — Figma `4687:5896` (40px, 10px radius). */
export const PUBLISH_FILTER_SELECT_SX = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    height: 40,
    bgcolor: '#fff',
    '& fieldset': { borderColor: publishInputBorder },
    '&:hover fieldset': { borderColor: publishInputBorder },
    '&.Mui-focused fieldset': {
      borderColor: publishPrimary,
      borderWidth: '1px',
    },
    '&.Mui-disabled': {
      bgcolor: '#fff',
      '& fieldset': { borderColor: publishInputBorder },
    },
  },
  '& .MuiSelect-select': {
    py: 0,
    px: '12px',
    pr: `${12 + PUBLISH_FILTER_SELECT_ICON_SIZE_PX + 8}px !important`,
    minHeight: '40px !important',
    height: 40,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    lineHeight: '21px',
    fontWeight: 400,
    color: publishInk,
  },
  '& .MuiSelect-icon': {
    width: PUBLISH_FILTER_SELECT_ICON_SIZE_PX,
    height: PUBLISH_FILTER_SELECT_ICON_SIZE_PX,
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'transparent',
    '& img': {
      width: PUBLISH_FILTER_SELECT_ICON_SIZE_PX,
      height: PUBLISH_FILTER_SELECT_ICON_SIZE_PX,
    },
  },
  '& .MuiSelect-icon.Mui-disabled': {
    opacity: 0.38,
  },
} as const

export const PUBLISH_FILTER_CHECKBOX_SX = {
  gap: '8px',
  '& .MuiFormControlLabel-root': {
    margin: 0,
    minHeight: '21px',
    alignItems: 'center',
    gap: '8px',
  },
  '& .MuiCheckbox-root': {
    p: 0,
    width: 16,
    height: 16,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '21px',
    color: publishInk,
  },
} as const

export const PUBLISH_SUMMARY_CARD_SX = {
  flex: 1,
  minWidth: 0,
  borderRadius: '14px',
  p: '17px',
  height: { xs: 'auto', md: '125px' },
  minHeight: '125px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
} as const

export const PUBLISH_SUMMARY_TEXT_COLUMN_SX = {
  flex: 1,
  minWidth: 0,
} as const
